import type { BookingRules } from '../../types/appointments.js';

// Booking-rule validation. PURE, and SHARED by the settings form and the zod
// schema at the router boundary — the `capError` precedent from the lead bot.
//
// ⚠️ ONE IMPLEMENTATION, TWO CALLERS. The form runs the same function the API
// runs, so a value the form accepts cannot be one the API then rejects, and the
// "there is no unlimited" rule below has exactly one place to keep honest.

export type BookingRuleField = keyof BookingRules;

export interface RuleBound {
  min: number;
  max: number;
  label: string;
  /** Shown under the input; explains the field rather than restating the bound. */
  hint: string;
}

/**
 * ⚠️ MAXPERDAY AND MAXPERSLOT START AT 1, NOT 0, and this is the lead-bot caps
 * lesson applied to a different screen. Someone typing 0 means "no limit" — and
 * 0 is precisely the value that must never be storable, because it would read as
 * either "unlimited" or "never bookable" depending on who wrote the code. Set a
 * high finite number instead; a calendar that should offer nothing is DISABLED,
 * which is a different field and says what it means.
 *
 * `minimumNoticeMinutes` and the buffers DO allow 0 — "bookable right now" and
 * "no gap needed" are real, sane policies with no ambiguity about what they mean.
 */
export const BOOKING_RULE_BOUNDS: Record<BookingRuleField, RuleBound> = {
  meetingIntervalMinutes: {
    min: 5,
    max: 480,
    label: 'Meeting interval',
    hint: 'The gap between the start times we offer. Not the length of the meeting.',
  },
  meetingDurationMinutes: {
    min: 5,
    max: 480,
    label: 'Meeting duration',
    hint: 'How long the appointment itself runs. This is the length of the calendar event.',
  },
  minimumNoticeMinutes: {
    min: 0,
    max: 43200,
    label: 'Minimum notice',
    hint: 'How soon from now a slot may be booked. 0 allows a booking for right now.',
  },
  dateRangeDays: {
    min: 1,
    max: 90,
    label: 'Date range',
    hint: 'How far ahead we will offer times.',
  },
  preBufferMinutes: {
    min: 0,
    max: 240,
    label: 'Buffer before',
    hint: 'Free time required immediately before a slot for it to be offered.',
  },
  postBufferMinutes: {
    min: 0,
    max: 240,
    label: 'Buffer after',
    hint: 'Free time required immediately after a slot for it to be offered.',
  },
  maxPerDay: {
    min: 1,
    max: 100,
    label: 'Maximum bookings per day',
    hint: 'Counts only bookings made through Kyck, across the whole calendar. Anything a salesperson books themselves takes up their time but does not count here.',
  },
  maxPerSlot: {
    min: 1,
    max: 20,
    label: 'Maximum bookings per slot',
    hint: 'How many bookings may share one start time, across the whole calendar.',
  },
};

/**
 * The reason `value` is not a legal setting for `field`, or null when it is.
 *
 * ⚠️ A BLANK BOX MUST REACH HERE AS NaN, NEVER AS 0. `Number('')` is 0, which for
 * the two caps is the one value that must not be storable — so the form converts
 * an empty input with `toRuleNumber` below rather than with `Number`.
 */
export function bookingRuleError(value: number, field: BookingRuleField): string | null {
  const bound = BOOKING_RULE_BOUNDS[field];
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    return `${bound.label} needs a whole number.`;
  }
  if (value < bound.min) {
    if (bound.min === 1 && value === 0) {
      return `${bound.label} cannot be 0 — there is no unlimited setting. Use a high number, or turn the calendar off.`;
    }
    return `${bound.label} must be at least ${bound.min}.`;
  }
  if (value > bound.max) {
    return `${bound.label} cannot be more than ${bound.max}.`;
  }
  return null;
}

/**
 * ⚠️ An EMPTY box becomes NaN, not 0 — so it fails validation with "needs a whole
 * number" rather than silently saving a cap of zero. The lead-bot `toCapNumber`
 * twin, for the same reason.
 */
export function toRuleNumber(raw: string): number {
  const trimmed = raw.trim();
  return trimmed === '' ? Number.NaN : Number(trimmed);
}

/** Every rule error in one pass, keyed by field. Empty object = all valid. */
export function bookingRuleErrors(rules: BookingRules): Partial<Record<BookingRuleField, string>> {
  const out: Partial<Record<BookingRuleField, string>> = {};
  for (const field of Object.keys(BOOKING_RULE_BOUNDS) as BookingRuleField[]) {
    const message = bookingRuleError(rules[field], field);
    if (message) out[field] = message;
  }
  return out;
}

/**
 * A stored rules block, with anything unreadable coerced to the DEFAULT.
 *
 * ⚠️ AT THE READ BOUNDARY, so a legacy doc or a console hand-edit resolves to a
 * sane policy for EVERY consumer rather than reaching one of them as a zero cap
 * or a NaN interval. Falling back to the default rather than refusing is
 * deliberate: a calendar that silently offers nothing is harder to notice than
 * one running on the defaults.
 */
export function resolveBookingRules(
  stored: Partial<BookingRules> | null | undefined,
  defaults: BookingRules,
): BookingRules {
  const out = { ...defaults };
  for (const field of Object.keys(BOOKING_RULE_BOUNDS) as BookingRuleField[]) {
    const value = stored?.[field];
    if (typeof value === 'number' && bookingRuleError(value, field) === null) {
      out[field] = value;
    }
  }
  return out;
}
